import {
  QIcon_exclamation_filled2,
  QIcon_glitter_primary,
  QIcon_refresh,
  QIcon_thumbs_down,
  QIcon_thumbs_up,
} from '@repo/icons/index'
import { useEffect, useState } from 'react'
import { AIParaphraseCorrection, AIParaphraseResponse } from '../../../../model/caf.interface'
import { useCafAIFeedbackV5, useCafAIParaphraseV5 } from '../../../../modules/apis/webview/caf'

import { useQueryClient } from '@tanstack/react-query'
import produce from 'immer'
import { throttle } from 'lodash'
import ReactHtmlParser from 'react-html-parser'
import apis from '../../../../modules/apis'
import useHtmlParserTransform from '../../../../modules/hooks/webview/caf/common/useHtmlParserTransform'
import { n_caf } from '../../../../modules/i18n/strings/auto/wording_webview'
import useTranslation from '../../../../modules/i18n/useTranslation'
import LoadingSpinner from '../../../common/LoadingSpinner'

const INITIAL_RESPONSE: AIParaphraseResponse = {
  correction: {
    correction_id: 0,
    feedback: 0,
    response: '',
    success: false,
  },
  corrections_count: 0,
}
interface Props {
  highlightId: any
  correction: AIParaphraseCorrection | null
  tag?: number
  tryCount: number
}
const AIParaphraseButton = ({
  highlightId,
  correction = INITIAL_RESPONSE.correction,
  tag = 0,
  tryCount = 0,
}: Props) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { transformHTMLNode } = useHtmlParserTransform()
  const [response, setResponse] = useState<AIParaphraseResponse>(INITIAL_RESPONSE)
  const { isLoading, mutate } = useCafAIParaphraseV5()
  const { mutate: updateFeedback } = useCafAIFeedbackV5({
    onSuccess: (data, [id, up]) => {
      if (data.status === 201) {
        // 뒤로가기 했을 경우 갱신이 안되므로 list api를 cache invalidate.
        queryClient.invalidateQueries([apis.caf.highlight.detail(highlightId)])
        setResponse(
          produce(response, (draft) => {
            const next =
              (draft.correction.feedback === 1 && up) || (draft.correction.feedback === 2 && !up) ? 0 : up ? 1 : 2
            draft.correction.feedback = next
          })
        )
      }
    },
  })
  console.log('highlightId', highlightId, correction, tag, response)

  const getAIparaphrases = throttle(() => {
    // 3초 throttling
    mutate(highlightId, {
      onSuccess: ({ data }) => {
        setResponse(data)
      },
    })
  }, 3000)

  useEffect(() => {
    setResponse(
      produce(response, (draft) => {
        draft.correction = correction
      })
    )
  }, [correction, tag])

  const { correction: phrase } = response
  const disabled = tryCount >= 2 || response.corrections_count >= 2
  const isSucceed = !isLoading && phrase?.correction_id > 0 && phrase?.success
  const isFailed = !isLoading && phrase?.correction_id > 0 && !phrase?.success
  const isLike = phrase?.feedback === 1
  const isDislike = phrase?.feedback === 2

  return (
    <div
      className="mt-[16px] mb-[8px] flex w-[100%] cursor-pointer items-center justify-center rounded-[6px] border border-gray-300 py-[12px]"
      style={{ backgroundColor: isFailed ? '#F7F9FB' : 'white', borderWidth: isFailed ? 0 : 1 }}
      onClick={!isSucceed && !isFailed ? getAIparaphrases : null}
    >
      {isLoading ? (
        <div className="flex flex-col items-center justify-center">
          <LoadingSpinner md show primary />
          <div className="text-sBody mt-[8px] text-center text-gray-600">{t(n_caf.highlight.ai_loading)}</div>
        </div>
      ) : isSucceed ? (
        <div className="flex flex-1 flex-col items-start justify-start px-[8px]">
          <span className="text-h5 text-purple-500">
            <QIcon_glitter_primary className="mr-[5px] inline h-[20px] w-[20px]" />
            {t(n_caf.highlight.ai_phrase)}
          </span>
          <div
            className="keep-all mt-[8px] font-medium"
            style={{
              fontSize: '14px',
              fontWeight: '500',
              lineHeight: '20px',
              letterSpacing: '-0.3px',
            }}
          >
            {ReactHtmlParser(phrase?.response || '', { transform: transformHTMLNode })}
          </div>
          <div className="flex w-[100%] flex-row items-center justify-end pr-[8px]">
            <span onClick={() => updateFeedback([phrase?.correction_id || 0, true])}>
              <QIcon_thumbs_up width={20} height={20} className="mr-[13px]" fill={isLike ? '#869AB8' : 'white'} />
            </span>
            <span onClick={() => updateFeedback([phrase?.correction_id || 0, false])}>
              <QIcon_thumbs_down width={20} height={20} fill={isDislike ? '#869AB8' : 'white'} />
            </span>
          </div>
        </div>
      ) : isFailed ? (
        <div className="flex flex-col items-center justify-center">
          <QIcon_exclamation_filled2 width={20} height={20} />
          <div className="text-h5 mt-[4px] text-gray-600">{t(n_caf.highlight.ai_error)}</div>
          {!disabled && (
            <div
              className="mt-[16px] flex-row items-center justify-center rounded-[6px] border border-gray-200 bg-white py-[9px] px-[16px]"
              onClick={getAIparaphrases}
            >
              <QIcon_refresh width={20} height={20} color="#7A5DE8" className="inline" />
              <span className="text-h5 ml-[8px] text-purple-500">{t(n_caf.highlight.ai_tryagain)}</span>
            </div>
          )}
        </div>
      ) : (
        <>
          <QIcon_glitter_primary className="mr-[5px] h-[20px] w-[20px]" />
          <span className="text-h5 font-weight-500 text-purple-500">{t(n_caf.highlight.ai_paraphrase)}</span>
        </>
      )}
    </div>
  )
}

export default AIParaphraseButton
